import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@googlemaps/js-api-loader";
import { MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface HeadquartersTimezoneInputProps {
  value: string;
  onChange: (value: string, timezone: string) => void;
  label?: string;
}

export const HeadquartersTimezoneInput = ({ 
  value, 
  onChange, 
  label
}: HeadquartersTimezoneInputProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const displayLabel = label || t("dashboard.governance.timezone.headquartersTimezone", "Headquarters timezone");

  useEffect(() => {
    const initAutocomplete = async () => {
      try {
        setIsLoading(true);
        
        // Get Google Maps API key from edge function using centralized client
        const { data, error } = await supabase.functions.invoke('get-google-maps-key');
        
        if (error) {
          console.error('Failed to get Google Maps API key:', error);
          return;
        }
        
        const apiKey = data?.apiKey;
        
        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places']
        });

        await loader.load();

        if (!inputRef.current) return;

        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['(cities)'],
          fields: ['address_components', 'geometry', 'name']
        });

        autocompleteRef.current.addListener('place_changed', async () => {
          const place = autocompleteRef.current?.getPlace();
          
          if (!place || !place.geometry?.location) {
            console.error('No place details available');
            return;
          }

          // Get timezone from coordinates
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const timestamp = Math.floor(Date.now() / 1000);

          try {
            const tzResponse = await fetch(
              `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`
            );
            
            const tzData = await tzResponse.json();
            
            if (tzData.status === 'OK') {
              const placeName = place.name || '';
              const timezone = tzData.timeZoneId || 'Europe/Paris';
              onChange(placeName, timezone);
            }
          } catch (error) {
            console.error('Error getting timezone:', error);
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onChange]);

  return (
    <div className="space-y-2">
      <Label htmlFor="headquarters-timezone" className="flex items-center gap-2">
        <MapPin className="h-4 w-4" />
        {displayLabel}
      </Label>
      <Input
        ref={inputRef}
        id="headquarters-timezone"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value, '')}
        placeholder={t("dashboard.governance.timezone.startTyping", "Start typing to select a city")}
        disabled={isLoading}
      />
      <p className="text-xs text-muted-foreground">
        {t("dashboard.governance.timezone.startTyping", "Start typing to select a city")}
      </p>
    </div>
  );
};